import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Badge } from 'primereact/badge';
import { TabView, TabPanel } from 'primereact/tabview';
import axiosInstance from '../../config/axios.config';
import { getProcessingStatusConfig } from '../../services/leadProcessingService';
import { determineReportType, getReportComponent } from '../../config/reportTypeMapping';
import { toast } from 'react-toastify';

/**
 * Lead Details Dialog - Same format as LeadDetails page but in overlay
 * Keeps the parent component (MLM tree / Leads modal) mounted underneath
 */
const LeadDetailsDialog = ({ visible, onHide, leadId, treeType }) => {
    const [loading, setLoading] = useState(true);
    const [leadData, setLeadData] = useState(null);
    const [attachedReports, setAttachedReports] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
    const [completion, setCompletion] = useState(null);

    const [activeMainTab, setActiveMainTab] = useState(0);
    const [activeReportTab, setActiveReportTab] = useState(0);
    const [reportTabsData, setReportTabsData] = useState({});

    const [documentModalVisible, setDocumentModalVisible] = useState(false);
    const [selectedDocumentForModal, setSelectedDocumentForModal] = useState(null);

    // Load lead details when dialog opens
    useEffect(() => {
        if (visible && leadId) {
            loadLeadDetails();
        }
    }, [visible, leadId]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!visible) {
            setLeadData(null);
            setAttachedReports([]);
            setDocuments([]);
            setCompletion(null);
            setActiveMainTab(0);
            setActiveReportTab(0);
            setReportTabsData({});
            setLoading(true);
        }
    }, [visible]);

    // Load report when Reports tab is opened
    useEffect(() => {
        if (activeMainTab === 1 && attachedReports.length > 0) {
            const firstReport = attachedReports[activeReportTab];
            if (firstReport) {
                loadReportData(firstReport.report_id);
            }
        }
    }, [activeMainTab, attachedReports]);

    const loadLeadDetails = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/leads/${leadId}/full-details${treeType ? `?tree=${treeType}` : ''}`);
            if (response.data.success) {
                const data = response.data.data;
                setLeadData(data.lead);
                setAttachedReports(data.reports || []);
                setDocuments(data.documents || []);
                setCompletion(data.completion || null);
                setCurrentDocumentIndex(0);
                setReportTabsData({});
            }
        } catch (error) {
            console.error('Error loading lead details:', error);
            toast.error('Failed to load lead details');
        } finally {
            setLoading(false);
        }
    };

    const loadReportData = async (reportId) => {
        if (reportTabsData[reportId]) return;

        try {
            const response = await axiosInstance.get(`/report-history/details/${reportId}`);
            if (response.data.success) {
                const fullReportData = response.data.data;
                let parsedReportData = fullReportData.report_json || fullReportData.report_data;
                if (typeof parsedReportData === 'string') {
                    try {
                        parsedReportData = JSON.parse(parsedReportData);
                    } catch (e) {
                        console.error('Error parsing report_json:', e);
                    }
                }
                setReportTabsData(prev => ({
                    ...prev,
                    [reportId]: { ...fullReportData, report_data: parsedReportData, report_json: parsedReportData }
                }));
            }
        } catch (error) {
            console.error('Error loading report data:', error);
            toast.error('Failed to load report data');
        }
    };

    // Helper functions - same as original LeadDetails.js
    const getDocumentIcon = (documentType) => {
        const iconMap = {
            'pan': 'pi-id-card',
            'aadhar': 'pi-id-card',
            'driving_licence': 'pi-car',
            'gst': 'pi-file',
            'bank_statement': 'pi-credit-card',
            'appointment_letter': 'pi-briefcase',
            'other': 'pi-file-o'
        };
        return iconMap[documentType?.toLowerCase()] || 'pi-file';
    };

    const navigateDocument = (direction) => {
        if (!documents || documents.length === 0) return;
        const newIndex = direction === 'next'
            ? (currentDocumentIndex + 1) % documents.length
            : currentDocumentIndex === 0 ? documents.length - 1 : currentDocumentIndex - 1;
        setCurrentDocumentIndex(newIndex);
    };

    const getStatusDisplay = (status) => getProcessingStatusConfig(status);

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
    };

    const formatProductType = (productType) => {
        if (!productType) return 'Not specified';
        const productTypeMap = {
            'new_personal_loan': 'New Personal Loan',
            'personal_loan_top_up': 'Personal Loan Top Up',
            'personal_loan_balance_transfer': 'Personal Loan Balance Transfer',
            'personal_loan_bt_top_up': 'Personal Loan BT + Top Up',
            'personal_loan_over_draft': 'Personal Loan Over Draft',
            'new_home_loan': 'New Home Loan',
            'home_loan_top_up': 'Home Loan Top Up',
            'home_loan_balance_transfer': 'Home Loan Balance Transfer',
            'home_loan_bt_top_up': 'Home Loan BT + Top Up',
            'home_loan_over_draft': 'Home Loan Over Draft',
            'home_loan_vendor_bt': 'Home Loan Vendor BT',
            'home_loan_assignment': 'Home Loan Assignment',
            'loan_against_property': 'Loan Against Property',
            'lap_top_up': 'LAP Top Up',
            'lap_balance_transfer': 'LAP Balance Transfer',
            'lap_bt_top_up': 'LAP BT + Top Up',
            'lap_over_draft': 'LAP Over Draft',
            'lap_vendor_bt': 'LAP Vendor BT',
            'car_loan': 'Car Loan',
            'car_over_draft': 'Car Over Draft',
            'educational_loan': 'Educational Loan',
            'equipment_loan': 'Equipment Loan'
        };
        return productTypeMap[productType] || productType;
    };

    const renderReportOutput = (reportData) => {
        if (!reportData) {
            return <div className="text-center p-4">No report data available</div>;
        }

        const reportType = determineReportType(reportData);
        const reportConfig = getReportComponent(reportType);

        if (!reportConfig || !reportConfig.component) {
            return (
                <div className="surface-50 p-4 border-round">
                    <div className="flex align-items-center gap-2 mb-3">
                        <i className="pi pi-info-circle text-blue-500"></i>
                        <span className="font-semibold text-900">Report Data (JSON View)</span>
                    </div>
                    <div className="surface-100 p-3 border-round">
                        <p className="text-sm text-600 mb-2"><strong>Report Type:</strong> {reportType}</p>
                        <p className="text-sm text-600 mb-2"><strong>Report Name:</strong> {reportData.report_name}</p>
                        <p className="text-sm text-600"><strong>Status:</strong> {reportData.status}</p>
                    </div>
                    <pre className="surface-200 p-3 border-round mt-3 overflow-auto text-sm" style={{maxHeight: '400px'}}>
                        {JSON.stringify(reportData.report_data || reportData, null, 2)}
                    </pre>
                </div>
            );
        }

        const OutputComponent = reportConfig.component;
        const componentProps = reportConfig.props ? reportConfig.props(reportData, reportData) : { reportData };
        const pdfPasswordHint = reportData?.pdf_password_hint || componentProps.pdfPasswordHint;
        const pdfDownloadUrl = reportData?.pdf_url || reportData?.pdf_download_url || componentProps.pdfDownloadUrl;

        return <OutputComponent {...componentProps} hideCredits={true} pdfPasswordHint={pdfPasswordHint} pdfDownloadUrl={pdfDownloadUrl} />;
    };

    const handleDownloadDocument = async (doc) => {
        const documentPath = doc?.url || doc?.path;
        const documentName = doc?.type || 'document';

        if (!documentPath) {
            toast.error('Document URL not available');
            return;
        }

        try {
            const params = new URLSearchParams({ path: documentPath, name: documentName });
            const response = await axiosInstance.get(`/leads/document/download?${params.toString()}`, { responseType: 'blob' });

            let filename = documentName;
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) filename = filenameMatch[1];
            } else {
                const extension = documentPath.split('.').pop();
                if (extension && !filename.includes('.')) filename = `${filename}.${extension}`;
            }

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
            toast.success('Document downloaded successfully');
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download document');
        }
    };

    return (
        <>
            <style>
                {`
                .lead-details-dialog .section-header {
                    background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
                    border-left: 4px solid #667eea;
                    padding: 1rem 1.5rem;
                    border-radius: 0 8px 8px 0;
                    margin-bottom: 1.5rem;
                }

                .lead-details-dialog .professional-card {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    border: 1px solid #e2e8f0;
                    transition: all 0.3s ease;
                    overflow: hidden;
                }

                .lead-details-dialog .field-icon {
                    color: #667eea;
                    font-size: 1.2rem;
                }
                `}
            </style>

            <Dialog
                visible={visible}
                onHide={onHide}
                header={null}
                footer={null}
                style={{ width: '95vw', maxWidth: '1400px' }}
                contentStyle={{ padding: 0 }}
                modal
                dismissableMask
                className="lead-details-dialog"
                pt={{
                    header: { className: 'hidden' },
                    closeButton: { className: 'hidden' }
                }}
            >
                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '500px' }}>
                        <ProgressSpinner />
                    </div>
                ) : !leadData ? (
                    <div className="text-center p-5">
                        <h3>Lead not found</h3>
                        <Button label="Close" icon="pi pi-times" onClick={onHide} />
                    </div>
                ) : (
                    <>
                        {/* Professional Header - Same as LeadDetails.js */}
                        <div className="surface-card p-4 shadow-2 border-round-lg" style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white'
                        }}>
                            <div className="flex align-items-center justify-content-between">
                                <div>
                                    <h1 className="text-3xl font-bold text-white mb-2">
                                        <i className="pi pi-user mr-3"></i>
                                        Lead Details
                                    </h1>
                                    <p className="text-white-alpha-80 m-0 text-lg">
                                        View and manage lead information, reports, and documents
                                    </p>
                                </div>
                                <div>
                                    <Button
                                        label="Close"
                                        icon="pi pi-times"
                                        onClick={onHide}
                                        className="p-button-outlined"
                                        style={{
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            border: '1px solid rgba(255,255,255,0.3)',
                                            color: 'white',
                                            borderRadius: '8px',
                                            fontWeight: '600'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Sticky Main Navigation - Same as LeadDetails.js */}
                        <div className="bg-white border-round shadow-3 z-5" style={{ maxHeight: 'calc(90vh - 120px)', overflow: 'auto' }}>
                            <TabView
                                activeIndex={activeMainTab}
                                onTabChange={(e) => setActiveMainTab(e.index)}
                                className="main-tabs"
                            >
                                {/* Lead Information Tab */}
                                <TabPanel
                                    header="Lead Information"
                                    leftIcon="pi pi-user mr-2"
                                >
                                    <div className="surface-card p-4 border-round-lg shadow-1 surface-50">
                                        <div className="p-fluid">
                                            {/* Personal Information Section */}
                                            <div className="section-header">
                                                <h4 className="m-0 font-bold text-900">
                                                    <i className="pi pi-user mr-2 text-primary"></i>
                                                    Personal Information
                                                </h4>
                                            </div>

                                            <div className="formgrid grid">
                                                <div className="field col-12 md:col-6 lg:col-3">
                                                    <label className="text-600 text-sm font-semibold mb-2 block">
                                                        <i className="pi pi-user mr-2 text-primary"></i>
                                                        Full Name *
                                                    </label>
                                                    <span className="text-900 font-bold">{leadData.full_name || 'Not provided'}</span>
                                                </div>

                                                <div className="field col-12 md:col-6 lg:col-3">
                                                    <label className="text-600 text-sm font-semibold mb-2 block">
                                                        <i className="pi pi-phone mr-2 text-primary"></i>
                                                        Mobile Number *
                                                    </label>
                                                    <span className="text-900 font-bold">{leadData.phone || 'Not provided'}</span>
                                                </div>

                                                <div className="field col-12 md:col-6 lg:col-3">
                                                    <label className="text-600 text-sm font-semibold mb-2 block">
                                                        <i className="pi pi-envelope mr-2 text-primary"></i>
                                                        Email Address *
                                                    </label>
                                                    <span className="text-900 font-bold">{leadData.email || 'Not provided'}</span>
                                                </div>

                                                <div className="field col-12 md:col-6 lg:col-3">
                                                    <label className="text-600 text-sm font-semibold mb-2 block">
                                                        <i className="pi pi-id-card mr-2 text-primary"></i>
                                                        PAN Number *
                                                    </label>
                                                    <span className="text-900 font-bold">{leadData.pan || 'Not provided'}</span>
                                                </div>
                                            </div>

                                            {/* Professional Information Section */}
                                            <div className="section-header">
                                                <h4 className="m-0 font-bold text-900">
                                                    <i className="pi pi-briefcase mr-2 text-primary"></i>
                                                    Professional & Financial Information
                                                </h4>
                                            </div>

                                            <div className="formgrid grid">
                                                <div className="field col-12 md:col-6 lg:col-3">
                                                    <label className="text-600 text-sm font-semibold mb-2 block">
                                                        <i className="pi pi-tag mr-2 text-primary"></i>
                                                        Product Type *
                                                    </label>
                                                    <span className="text-900 font-bold">{formatProductType(leadData.product_type_detailed)}</span>
                                                </div>

                                                <div className="field col-12 md:col-6 lg:col-3">
                                                    <label className="text-600 text-sm font-semibold mb-2 block">
                                                        <i className="pi pi-briefcase mr-2 text-primary"></i>
                                                        Employment Type *
                                                    </label>
                                                    <span className="text-900 font-bold">{leadData.employment_type?.replace('_', ' ').toUpperCase() || 'Not specified'}</span>
                                                </div>

                                                <div className="field col-12 md:col-6 lg:col-3">
                                                    <label className="text-600 text-sm font-semibold mb-2 block">
                                                        <i className="pi pi-dollar mr-2 text-primary"></i>
                                                        Loan Amount Required *
                                                    </label>
                                                    <span className="text-900 font-bold">{formatCurrency(leadData.loan_amount_required) || 'Not specified'}</span>
                                                </div>

                                                <div className="field col-12 md:col-6 lg:col-3">
                                                    <label className="text-600 text-sm font-semibold mb-2 block">
                                                        <i className="pi pi-flag mr-2 text-primary"></i>
                                                        Lead Status *
                                                    </label>
                                                    {(() => { const c = getStatusDisplay(leadData.lead_status); return <Tag value={c.label} severity={c.severity} icon={`pi ${c.icon}`} />; })()}
                                                </div>

                                                {leadData.company_name && (
                                                    <div className="field col-12 md:col-6">
                                                        <label className="text-600 text-sm font-semibold mb-2 block">
                                                            <i className="pi pi-building mr-2 text-primary"></i>
                                                            Company Name *
                                                        </label>
                                                        <span className="text-900 font-bold">{leadData.company_name}</span>
                                                    </div>
                                                )}

                                                {leadData.net_salary && (
                                                    <div className="field col-12 md:col-6">
                                                        <label className="text-600 text-sm font-semibold mb-2 block">
                                                            <i className="pi pi-wallet mr-2 text-primary"></i>
                                                            Net Salary *
                                                        </label>
                                                        <span className="text-900 font-bold">{formatCurrency(leadData.net_salary)}</span>
                                                    </div>
                                                )}

                                                {leadData.business_type && (
                                                    <div className="field col-12 md:col-6">
                                                        <label className="text-600 text-sm font-semibold mb-2 block">
                                                            <i className="pi pi-shop mr-2 text-primary"></i>
                                                            Business Type *
                                                        </label>
                                                        <span className="text-900 font-bold">{leadData.business_type}</span>
                                                    </div>
                                                )}

                                                {leadData.annual_turnover && (
                                                    <div className="field col-12 md:col-6">
                                                        <label className="text-600 text-sm font-semibold mb-2 block">
                                                            <i className="pi pi-chart-line mr-2 text-primary"></i>
                                                            Annual Turnover *
                                                        </label>
                                                        <span className="text-900 font-bold">{formatCurrency(leadData.annual_turnover)}</span>
                                                    </div>
                                                )}

                                                {leadData.profession_type && (
                                                    <div className="field col-12 md:col-6">
                                                        <label className="text-600 text-sm font-semibold mb-2 block">
                                                            <i className="pi pi-user-plus mr-2 text-primary"></i>
                                                            Profession Type
                                                        </label>
                                                        <span className="text-900 font-bold">{leadData.profession_type}</span>
                                                    </div>
                                                )}

                                                {leadData.gross_receipts_per_annum && (
                                                    <div className="field col-12 md:col-6">
                                                        <label className="text-600 text-sm font-semibold mb-2 block">
                                                            <i className="pi pi-money-bill mr-2 text-primary"></i>
                                                            Gross Receipts Per Annum
                                                        </label>
                                                        <span className="text-900 font-bold">{formatCurrency(leadData.gross_receipts_per_annum)}</span>
                                                    </div>
                                                )}

                                                <div className="field col-12 md:col-6 lg:col-3">
                                                    <label className="text-600 text-sm font-semibold mb-2 block">
                                                        <i className="pi pi-users mr-2 text-primary"></i>
                                                        Referred By
                                                    </label>
                                                    <span className="text-900 font-bold">{leadData.referred_by || 'Not provided'}</span>
                                                </div>

                                                <div className="field col-12 md:col-6 lg:col-3">
                                                    <label className="text-600 text-sm font-semibold mb-2 block">
                                                        <i className="pi pi-building mr-2 text-primary"></i>
                                                        Preferred Banks
                                                    </label>
                                                    <span className="text-900 font-bold">
                                                        {Array.isArray(leadData.preferred_bank) && leadData.preferred_bank.length > 0
                                                            ? leadData.preferred_bank.join(', ')
                                                            : leadData.preferred_bank || 'Not provided'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Address Information Section */}
                                            {(leadData.address || leadData.city || leadData.state || leadData.pincode) && (
                                                <>
                                                    <div className="section-header">
                                                        <h4 className="m-0 font-bold text-900">
                                                            <i className="pi pi-map-marker mr-2 text-primary"></i>
                                                            Address Information
                                                        </h4>
                                                    </div>

                                                    <div className="formgrid grid">
                                                        {leadData.address && (
                                                            <div className="field col-12">
                                                                <label className="text-600 text-sm font-semibold mb-2 block">
                                                                    <i className="pi pi-home mr-2 text-primary"></i>
                                                                    Address
                                                                </label>
                                                                <span className="text-900 font-bold">{leadData.address}</span>
                                                            </div>
                                                        )}
                                                        {leadData.city && (
                                                            <div className="field col-12 md:col-6 lg:col-3">
                                                                <label className="text-600 text-sm font-semibold mb-2 block">
                                                                    <i className="pi pi-building mr-2 text-primary"></i>
                                                                    City
                                                                </label>
                                                                <span className="text-900 font-bold">{leadData.city}</span>
                                                            </div>
                                                        )}
                                                        {leadData.state && (
                                                            <div className="field col-12 md:col-6 lg:col-3">
                                                                <label className="text-600 text-sm font-semibold mb-2 block">
                                                                    <i className="pi pi-map mr-2 text-primary"></i>
                                                                    State
                                                                </label>
                                                                <span className="text-900 font-bold">{leadData.state}</span>
                                                            </div>
                                                        )}
                                                        {leadData.pincode && (
                                                            <div className="field col-12 md:col-6 lg:col-3">
                                                                <label className="text-600 text-sm font-semibold mb-2 block">
                                                                    <i className="pi pi-inbox mr-2 text-primary"></i>
                                                                    Pincode
                                                                </label>
                                                                <span className="text-900 font-bold">{leadData.pincode}</span>
                                                            </div>
                                                        )}
                                                        {leadData.residency && (
                                                            <div className="field col-12 md:col-6 lg:col-3">
                                                                <label className="text-600 text-sm font-semibold mb-2 block">
                                                                    <i className="pi pi-globe mr-2 text-primary"></i>
                                                                    Residency
                                                                </label>
                                                                <span className="text-900 font-bold">{leadData.residency}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}

                                            {/* Loan Details Section */}
                                            {(leadData.loan_amount || leadData.interest_rate || leadData.period || leadData.emi) && (
                                                <>
                                                    <div className="section-header">
                                                        <h4 className="m-0 font-bold text-900">
                                                            <i className="pi pi-calculator mr-2 text-primary"></i>
                                                            Loan Details
                                                        </h4>
                                                    </div>

                                                    <div className="formgrid grid">
                                                        {leadData.loan_amount && (
                                                            <div className="field col-12 md:col-6 lg:col-3">
                                                                <label className="text-600 text-sm font-semibold mb-2 block">
                                                                    <i className="pi pi-dollar mr-2 text-primary"></i>
                                                                    Loan Amount
                                                                </label>
                                                                <span className="text-900 font-bold">{formatCurrency(leadData.loan_amount)}</span>
                                                            </div>
                                                        )}
                                                        {leadData.interest_rate && (
                                                            <div className="field col-12 md:col-6 lg:col-3">
                                                                <label className="text-600 text-sm font-semibold mb-2 block">
                                                                    <i className="pi pi-percentage mr-2 text-primary"></i>
                                                                    Interest Rate
                                                                </label>
                                                                <span className="text-900 font-bold">{leadData.interest_rate}%</span>
                                                            </div>
                                                        )}
                                                        {leadData.period && (
                                                            <div className="field col-12 md:col-6 lg:col-3">
                                                                <label className="text-600 text-sm font-semibold mb-2 block">
                                                                    <i className="pi pi-calendar mr-2 text-primary"></i>
                                                                    Period
                                                                </label>
                                                                <span className="text-900 font-bold">{leadData.period} months</span>
                                                            </div>
                                                        )}
                                                        {leadData.emi && (
                                                            <div className="field col-12 md:col-6 lg:col-3">
                                                                <label className="text-600 text-sm font-semibold mb-2 block">
                                                                    <i className="pi pi-credit-card mr-2 text-primary"></i>
                                                                    EMI
                                                                </label>
                                                                <span className="text-900 font-bold">{formatCurrency(leadData.emi)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}

                                            {/* CIBIL Information Section */}
                                            {(leadData.cibil_check_done || leadData.cibil_score) && (
                                                <>
                                                    <div className="section-header">
                                                        <h4 className="m-0 font-bold text-900">
                                                            <i className="pi pi-check-circle mr-2 text-primary"></i>
                                                            CIBIL Information
                                                        </h4>
                                                    </div>

                                                    <div className="formgrid grid">
                                                        <div className="field col-12 md:col-6 lg:col-3">
                                                            <label className="text-600 text-sm font-semibold mb-2 block">
                                                                <i className="pi pi-verified mr-2 text-primary"></i>
                                                                CIBIL Check Status
                                                            </label>
                                                            <Tag value={leadData.cibil_check_done ? 'COMPLETED' : 'PENDING'}
                                                                 severity={leadData.cibil_check_done ? 'success' : 'warning'} />
                                                        </div>
                                                        {leadData.cibil_score && (
                                                            <div className="field col-12 md:col-6 lg:col-3">
                                                                <label className="text-600 text-sm font-semibold mb-2 block">
                                                                    <i className="pi pi-star mr-2 text-primary"></i>
                                                                    CIBIL Score
                                                                </label>
                                                                <span className="text-900 font-bold">{leadData.cibil_score}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}

                                            {/* Additional Notes */}
                                            {leadData?.note && (
                                                <>
                                                    <div className="section-header">
                                                        <h4 className="m-0 font-bold text-900">
                                                            <i className="pi pi-comment mr-2 text-primary"></i>
                                                            Additional Notes
                                                        </h4>
                                                    </div>
                                                    <div className="field col-12">
                                                        <div className="surface-100 p-3 border-round">
                                                            <span className="text-900 line-height-3 white-space-pre-wrap">
                                                                {leadData.note}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </TabPanel>

                                {/* Attached Reports Tab with Sub-Tabs */}
                                <TabPanel
                                    header={
                                        <span>
                                            Attached Reports
                                            {attachedReports.length > 0 && (
                                                <Badge value={attachedReports.length} severity="info" className="ml-2" />
                                            )}
                                        </span>
                                    }
                                    leftIcon="pi pi-file mr-2"
                                >
                                    <div className="surface-card p-4 border-round-lg shadow-1">
                                        {attachedReports.length > 0 ? (
                                            <>
                                                <div className="flex align-items-center gap-2 mb-4 pb-3 border-bottom-1 surface-border">
                                                    <i className="pi pi-file text-primary text-xl"></i>
                                                    <h3 className="m-0 text-900 font-bold uppercase">Report Viewer</h3>
                                                    <span className="text-600">({attachedReports.length} reports)</span>
                                                </div>

                                                <TabView
                                                    activeIndex={activeReportTab}
                                                    onTabChange={(e) => {
                                                        setActiveReportTab(e.index);
                                                        const selectedReport = attachedReports[e.index];
                                                        if (selectedReport) {
                                                            loadReportData(selectedReport.report_id);
                                                        }
                                                    }}
                                                    className="report-sub-tabs"
                                                >
                                                    {attachedReports.map((report) => (
                                                        <TabPanel
                                                            key={report.report_id}
                                                            header={
                                                                <div className="flex align-items-center gap-2">
                                                                    <i className="pi pi-file-pdf text-sm"></i>
                                                                    <span className="font-medium">
                                                                        {report.report_name}
                                                                    </span>
                                                                </div>
                                                            }
                                                        >
                                                            <div className="report-content">
                                                                {reportTabsData[report.report_id] ? (
                                                                    renderReportOutput(reportTabsData[report.report_id])
                                                                ) : (
                                                                    <div className="flex justify-content-center align-items-center p-5">
                                                                        <div className="text-center">
                                                                            <ProgressSpinner style={{width: '50px', height: '50px'}} />
                                                                            <p className="text-600 mt-3">Loading report data...</p>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </TabPanel>
                                                    ))}
                                                </TabView>
                                            </>
                                        ) : (
                                            <div className="text-center p-5">
                                                <div className="surface-100 border-round-3xl w-5rem h-5rem flex align-items-center justify-content-center mx-auto mb-3">
                                                    <i className="pi pi-inbox text-3xl text-primary"></i>
                                                </div>
                                                <h4 className="text-900 font-bold mb-2">No Reports Attached</h4>
                                                <p className="text-600 mb-3 line-height-3">
                                                    This lead doesn't have any reports attached yet. Reports will appear here when they are generated and attached to this lead.
                                                </p>
                                                <div className="surface-100 p-3 border-round inline-block">
                                                    <span className="text-sm text-700 font-medium">
                                                        <i className="pi pi-lightbulb mr-2 text-yellow-600"></i>
                                                        Use the report generation tools to create reports for this lead
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </TabPanel>

                                {/* Documents Tab */}
                                <TabPanel
                                    header={
                                        <span>
                                            Documents
                                            {documents.length > 0 && (
                                                <Badge value={documents.length} severity="info" className="ml-2" />
                                            )}
                                        </span>
                                    }
                                    leftIcon="pi pi-folder mr-2"
                                >
                                    <div className="surface-card p-4 border-round-lg shadow-1">
                                        {/* Document Completion Status Section */}
                                        {completion && (
                                            <div className="mb-6">
                                                {completion.missing_documents && completion.missing_documents.length > 0 ? (
                                                    <div>
                                                        <div className="flex align-items-center gap-2 mb-3 pb-2 border-bottom-1 surface-border">
                                                            <i className="pi pi-exclamation-triangle text-orange-500 text-xl"></i>
                                                            <h3 className="m-0 text-900 font-bold uppercase">Missing Documents</h3>
                                                            <Badge value={completion.missing_documents.length} severity="warning" />
                                                        </div>

                                                        <div className="surface-50 border-round-lg p-4">
                                                            <div className="mb-3">
                                                                <span className="text-orange-600 font-medium">
                                                                    The following documents are missing for complete loan application:
                                                                </span>
                                                            </div>

                                                            <div className="grid">
                                                                {completion.missing_documents.map((doc, index) => (
                                                                    <div key={index} className="col-12 md:col-6 lg:col-4 mb-2">
                                                                        <div className="flex align-items-center gap-2 p-2 border-round-md surface-100">
                                                                            <i className="pi pi-circle-fill text-orange-500" style={{ fontSize: '8px' }}></i>
                                                                            <span className="text-900 font-medium">{doc}</span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            <div className="mt-3 p-3 border-round-md bg-orange-50 border-left-3 border-orange-500">
                                                                <div className="flex align-items-center gap-2">
                                                                    <i className="pi pi-info-circle text-orange-600"></i>
                                                                    <span className="text-orange-800 font-medium">
                                                                        Completion: {completion.completion_percentage || 0}%
                                                                        ({completion.total_completed || 0} of {completion.total_required || 0} documents uploaded
                                                                        {completion.newly_added_count > 0 && (
                                                                            <span className="text-blue-600" title={completion.newly_added_documents?.map(d => `${d.document_type} by ${d.uploaded_by}`).join(', ')}>
                                                                                , {completion.newly_added_count} via sharing
                                                                            </span>
                                                                        )})
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div className="flex align-items-center gap-2 mb-3 pb-2 border-bottom-1 surface-border">
                                                            <i className="pi pi-check-circle text-green-500 text-xl"></i>
                                                            <h3 className="m-0 text-900 font-bold uppercase">Document Status</h3>
                                                            <Badge value="Complete" severity="success" />
                                                        </div>

                                                        <div className="surface-50 border-round-lg p-4">
                                                            <div className="p-3 border-round-md bg-green-50 border-left-3 border-green-500">
                                                                <div className="flex align-items-center gap-2">
                                                                    <i className="pi pi-check-circle text-green-600"></i>
                                                                    <span className="text-green-800 font-medium">
                                                                        All required documents have been uploaded!
                                                                        Completion: {completion.completion_percentage || 100}%
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {documents && documents.length > 0 ? (
                                            <>
                                                <div className="flex align-items-center gap-2 mb-4 pb-3 border-bottom-1 surface-border">
                                                    <i className="pi pi-images text-primary text-xl"></i>
                                                    <h3 className="m-0 text-900 font-bold uppercase">Document Gallery</h3>
                                                    <span className="text-600">({documents.length} documents)</span>
                                                </div>

                                                {/* Current Document Display with Side Navigation */}
                                                <div className="surface-50 border-round-lg p-4 mb-4 relative min-h-30rem">
                                                    {/* Left Navigation Arrow */}
                                                    <Button
                                                        icon="pi pi-chevron-left"
                                                        className="p-button-rounded p-button-outlined absolute"
                                                        onClick={() => navigateDocument('prev')}
                                                        disabled={documents.length <= 1}
                                                        style={{
                                                            left: '10px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            width: '40px',
                                                            height: '40px'
                                                        }}
                                                    />

                                                    {/* Right Navigation Arrow */}
                                                    <Button
                                                        icon="pi pi-chevron-right"
                                                        className="p-button-rounded p-button-outlined absolute"
                                                        onClick={() => navigateDocument('next')}
                                                        disabled={documents.length <= 1}
                                                        style={{
                                                            right: '10px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            width: '40px',
                                                            height: '40px'
                                                        }}
                                                    />

                                                    <div className="text-center">
                                                        {(() => {
                                                            const currentDoc = documents[currentDocumentIndex];
                                                            const fileUrl = currentDoc?.url || currentDoc?.path || currentDoc?.file_url || currentDoc?.s3_url;
                                                            const fileExtension = fileUrl?.split('.').pop()?.toLowerCase();

                                                            if (fileUrl) {
                                                                if (fileExtension?.match(/^(jpg|jpeg|png|gif|bmp|webp)$/i)) {
                                                                    return (
                                                                        <img
                                                                            src={fileUrl}
                                                                            alt={currentDoc?.type || 'Document'}
                                                                            className="border-round shadow-2"
                                                                            style={{
                                                                                maxWidth: 'calc(100% - 100px)',
                                                                                maxHeight: '300px',
                                                                                objectFit: 'contain'
                                                                            }}
                                                                        />
                                                                    );
                                                                } else if (fileExtension === 'pdf') {
                                                                    return (
                                                                        <iframe
                                                                            src={`${fileUrl}#view=FitH&toolbar=0&navpanes=0`}
                                                                            className="border-round shadow-2 border-none bg-white mx-auto block"
                                                                            style={{
                                                                                width: 'calc(100% - 100px)',
                                                                                height: '300px'
                                                                            }}
                                                                            title={currentDoc?.type || 'PDF Document'}
                                                                        />
                                                                    );
                                                                }
                                                            }

                                                            return (
                                                                <div className="surface-200 border-round p-4 flex flex-column align-items-center justify-content-center mx-auto" style={{
                                                                    height: '300px',
                                                                    maxWidth: 'calc(100% - 100px)'
                                                                }}>
                                                                    <i className={`pi ${getDocumentIcon(currentDoc?.type)} text-6xl text-primary mb-3`}></i>
                                                                    <span className="text-900 font-bold text-lg">{currentDoc?.type || 'Document'}</span>
                                                                    <span className="text-600 text-sm">Document preview not available</span>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>

                                                    <h4 className="text-900 font-bold text-center mt-3 mb-3">
                                                        {documents[currentDocumentIndex]?.type?.replace('_', ' ') || 'Document'}
                                                    </h4>

                                                    {/* Action Buttons */}
                                                    <div className="flex justify-content-center gap-3">
                                                        <Button
                                                            label="Open Full Size"
                                                            icon="pi pi-eye"
                                                            className="p-button-outlined"
                                                            onClick={() => {
                                                                setSelectedDocumentForModal(documents[currentDocumentIndex]);
                                                                setDocumentModalVisible(true);
                                                            }}
                                                        />
                                                        <Button
                                                            label="Download"
                                                            icon="pi pi-download"
                                                            className="p-button-text"
                                                            onClick={() => handleDownloadDocument(documents[currentDocumentIndex])}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Document Counter and Dots */}
                                                <div className="text-center mb-3">
                                                    <span className="text-600 font-semibold mb-3 block">
                                                        {currentDocumentIndex + 1} of {documents.length}
                                                    </span>

                                                    <div className="flex justify-content-center gap-1">
                                                        {documents.map((_, index) => (
                                                            <div
                                                                key={index}
                                                                className={`border-round-3xl cursor-pointer transition-all transition-duration-300 ${
                                                                    index === currentDocumentIndex ? 'bg-primary' : 'surface-300'
                                                                }`}
                                                                style={{
                                                                    width: '12px',
                                                                    height: '12px'
                                                                }}
                                                                onClick={() => setCurrentDocumentIndex(index)}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-center p-5">
                                                <div className="surface-100 border-round-3xl w-6rem h-6rem flex align-items-center justify-content-center mx-auto mb-4">
                                                    <i className="pi pi-folder-open text-4xl text-primary"></i>
                                                </div>
                                                <h3 className="text-900 font-bold mb-2">No Documents Found</h3>
                                                <p className="text-600 mb-4 line-height-3">
                                                    This lead doesn't have any documents uploaded yet. Documents will appear here when they are added to the lead.
                                                </p>
                                                <div className="surface-100 p-3 border-round inline-block">
                                                    <span className="text-sm text-700 font-medium">
                                                        <i className="pi pi-info-circle mr-2"></i>
                                                        Documents are managed through the lead management system
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </TabPanel>

                            </TabView>
                        </div>
                    </>
                )}
            </Dialog>

            {/* Document Full Size Modal */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-file text-primary text-xl"></i>
                        <span className="font-bold text-lg">
                            {selectedDocumentForModal?.type?.replace('_', ' ') || 'Document Viewer'}
                        </span>
                    </div>
                }
                visible={documentModalVisible}
                onHide={() => {
                    setDocumentModalVisible(false);
                    setSelectedDocumentForModal(null);
                }}
                className="w-9 h-screen"
                contentClassName="p-0"
                headerClassName="p-3 surface-50 border-bottom-2 surface-border"
                maximizable
                modal
                blockScroll
            >
                {selectedDocumentForModal && (
                    <div className="w-full h-full flex flex-column">
                        <div className="surface-100 p-3 flex align-items-center justify-content-between border-bottom-1 surface-border">
                            <div className="flex align-items-center gap-2">
                                <i className={`pi ${getDocumentIcon(selectedDocumentForModal.type)} text-primary`}></i>
                                <span className="font-semibold text-900">{selectedDocumentForModal.type}</span>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    label="Download"
                                    icon="pi pi-download"
                                    className="p-button-outlined p-button-sm"
                                    onClick={() => handleDownloadDocument(selectedDocumentForModal)}
                                />
                                <Button
                                    label="Open in New Tab"
                                    icon="pi pi-external-link"
                                    className="p-button-text p-button-sm"
                                    onClick={() => {
                                        const fileUrl = selectedDocumentForModal?.url || selectedDocumentForModal?.path;
                                        if (fileUrl) window.open(fileUrl, '_blank');
                                    }}
                                />
                            </div>
                        </div>

                        <div className="flex-1 p-3 overflow-hidden">
                            {(() => {
                                const fileUrl = selectedDocumentForModal?.url || selectedDocumentForModal?.path;
                                const fileExtension = fileUrl?.split('.').pop()?.toLowerCase();

                                if (fileUrl) {
                                    if (fileExtension?.match(/^(jpg|jpeg|png|gif|bmp|webp)$/i)) {
                                        return (
                                            <div className="w-full h-full flex align-items-center justify-content-center">
                                                <img
                                                    src={fileUrl}
                                                    alt={selectedDocumentForModal?.type || 'Document'}
                                                    className="border-round shadow-2"
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '100%',
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                            </div>
                                        );
                                    } else if (fileExtension === 'pdf') {
                                        return (
                                            <iframe
                                                src={`${fileUrl}#view=FitH`}
                                                className="w-full h-full border-round border-none bg-white"
                                                title={selectedDocumentForModal?.type || 'PDF Document'}
                                            />
                                        );
                                    }
                                }

                                return (
                                    <div className="w-full h-full flex align-items-center justify-content-center">
                                        <div className="text-center">
                                            <i className={`pi ${getDocumentIcon(selectedDocumentForModal?.type)} text-6xl text-primary mb-3`}></i>
                                            <h4 className="text-900 font-bold mb-2">{selectedDocumentForModal?.type || 'Document'}</h4>
                                            <p className="text-600">Document preview not available</p>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default LeadDetailsDialog;
